import React, { useEffect, useState } from 'react';
import { ListSkeletonLoader } from '../../Components';
import { TrainingProviderCard } from './components';
import { useApi } from '../../Helpers/ApiHook';
import { FilterSidebar, SearchInput } from '../../Components/SearchFilters';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from 'rsuite';

const TrainingProviders = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { get } = useApi();

    // State for mobile filter sidebar visibility & filters
    const [filters, setFilters] = useState({
        q: "",
        states: "",
        cities: "",
        specialization: "",
    });
    const [pageState, setPageState] = useState({
        page: 1,
        limit: 10,
        totalRecords: 0,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [trainingProviderList, setTrainingProviderList] = useState([]);

    // Accepts params so we always call API with latest values
    const _getTrainingProviders = async (params = {}) => {
        setIsLoading(true);
        try {
            const response = await get("trainingprovider/list", {
                params: {
                    states: params.states ?? "",
                    cities: params.cities ?? "",
                    searchText: params.searchText ?? "", // map q/trainer type here
                    page: params.page ?? 1,
                    limit: params.limit ?? 10
                }
            });

            if (response?.data) {
                // API shape: response.data -> list
                setTrainingProviderList(response?.data || []);
                setPageState((prevState) => ({ ...prevState, totalRecords: response?.meta?.total || response?.data?.length || 0 }))
            } else {
                setTrainingProviderList([]);
            }
        } catch (error) {
            console.error("Error fetching TrainingProviders: ", error);
            setTrainingProviderList([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Run on mount and whenever URL search params change
    useEffect(() => {
        const states = searchParams.get('states') ?? "";
        const cities = searchParams.get('cities') ?? "";
        const q = searchParams.get('q') ?? "";
        const page = Number(searchParams.get('page') ?? 1);
        const limit = Number(searchParams.get('limit') ?? 10);

        // keep local filter state in sync
        setFilters((prev) => ({ ...prev, states, cities, q, page, limit }));
        setPageState((prev) => ({ ...prev, page, limit }));

        // Call API with mapped params
        _getTrainingProviders({
            states,
            cities,
            searchText: q, // if your API expects a different field, change mapping here
            page,
            limit
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onChangePage = async ({ keyName = "", value = "" }) => {
        const params = new URLSearchParams(searchParams.toString());
        await params.set(keyName, value);
        await setPageState((prevState) => ({ ...prevState, [keyName]: value }))
    }

    // Pagination Component (Simplified)
    const PaginationComponent = () => {
        if (pageState.totalRecords > 10) {
            return (
                <Pagination
                    layout={['total', '-', 'pager', '-', 'limit']}
                    size={'md'}
                    prev={true}
                    next={true}
                    // first={true}
                    // last={true}
                    ellipsis={false}
                    boundaryLinks={false}
                    total={pageState.totalRecords}
                    limit={pageState.limit || 10}
                    limitOptions={[10, 20, 30, 50, 100]}
                    maxButtons={5}
                    activePage={pageState.page || 1}
                    onChangePage={(pageNo) => { onChangePage({ keyName: "page", value: pageNo }) }}
                    onChangeLimit={(pageNo) => { onChangePage({ keyName: "limit", value: pageNo }) }}
                />
            );
        } else {
            <></>
        }
    };


    return (
        <div className="w-full flex flex-col">
            {/* Header Section */}
            <section className="w-full bg-gradient-radial lg:pt-20 pt-20 lg:pb-5 pb-5 lg:py-28">
                <div className="w-full lg:w-full flex flex-col items-center py-12">
                    <img className="md:w-[22rem] w-[15rem] h-auto" src="/assets/FWPI-logo.png" alt="FWPI Logo" />
                </div>

                <div className='w-full flex flex-col justify-between items-center'>
                    <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center">
                        Coach Directory
                    </h2>
                    <p className='mt-4 text-md text-center text-white py-2 px-4 lg:px-48 font-urbanist'>
                        Over 10,000 users matched themselves with the right coach on FWPI .
                    </p>
                </div>
            </section>
            <div className='w-full bg-gradient-radial'>
                <div className="container mx-auto px-4 sm:px-2 lg:px-4">
                    <div className="max-w-7xl mx-auto flex">
                        {/* DESKTOP Filter Sidebar */}
                        <aside className="hidden lg:block w-56 flex-shrink-0 pt-8">
                            <FilterSidebar />
                        </aside>

                        {/* Main Content Area */}
                        <main className="flex-grow py-4 md:py-8 pl-0 md:pl-8 pr-0 md:pr-0">
                            {/* Search and Specialization Bar */}
                            <SearchInput />

                            {/* Trainer Listings */}
                            <div className="md:p-4 md:bg-gradient-radial md:rounded-xl">
                                {
                                    (
                                        isLoading ?
                                            <ListSkeletonLoader count={4} />
                                            : (
                                                trainingProviderList?.length > 0 ?
                                                    trainingProviderList.map((trainer, index) => (
                                                        // prefer a unique id key if available
                                                        <TrainingProviderCard key={trainer?.id ?? index} {...trainer} />
                                                    ))
                                                    :
                                                    <div className="w-full flex flex-col items-center">
                                                        <p className="text-md text-center text-gray-600 py-2 px-4 lg:px-48 font-urbanist">Data not available</p>
                                                    </div>
                                            )
                                    )
                                }

                                {/* Pagination */}
                                <PaginationComponent />
                            </div>

                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TrainingProviders };
